import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  User, Mail, MapPin, Phone, Shield, Zap, Sun, Home,
  ArrowLeft, Save, Edit2, TrendingUp, Activity, LogOut,
  CreditCard, Building2, Check, AlertTriangle, Gauge,
  Download, Trash2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', address: '', phone: '' });
  const [ribData, setRibData] = useState({ iban: '', bic: '', titulaire: '' });
  const [ribSaved, setRibSaved] = useState(null);
  const [editingRib, setEditingRib] = useState(false);
  const [savingRib, setSavingRib] = useState(false);
  const [linkySerial, setLinkySerial] = useState('');
  const [editingLinky, setEditingLinky] = useState(false);
  const [savingLinky, setSavingLinky] = useState(false);
  const [profileComplete, setProfileComplete] = useState(true);
  const [missingFields, setMissingFields] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/users/me/profile`, { headers });
      setProfile(res.data);
      setStats(res.data.stats || {});
      setFormData({
        name: res.data.user?.name || '',
        address: res.data.profile?.address || res.data.user?.address || '',
        phone: res.data.profile?.phone || '',
      });
      if (res.data.rib?.iban_masked) {
        setRibSaved(res.data.rib);
        setRibData({ iban: '', bic: res.data.rib.bic || '', titulaire: res.data.rib.titulaire || '' });
      }
      setLinkySerial(res.data.linky_serial || res.data.profile?.linky_serial || '');
      setProfileComplete(res.data.profile_complete !== false);
      setMissingFields(res.data.missing_fields || []);
    } catch (error) {
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/api/users/me/profile`, formData, { headers });
      toast.success('Profil mis à jour');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSaveRib = async () => {
    if (!ribData.iban || !ribData.bic || !ribData.titulaire) {
      toast.error('Tous les champs RIB sont requis');
      return;
    }
    setSavingRib(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.put(`${API_URL}/api/users/me/rib`, ribData, { headers });
      toast.success('RIB enregistré');
      setRibSaved({ iban_masked: res.data.iban_masked, bic: ribData.bic, titulaire: ribData.titulaire });
      setEditingRib(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSavingRib(false);
    }
  };

  const handleSaveLinky = async () => {
    if (!linkySerial || linkySerial.trim().length < 5) {
      toast.error('Le numéro de série Linky est requis (min. 5 caractères)');
      return;
    }
    setSavingLinky(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/api/users/me/profile`, { linky_serial: linkySerial.trim() }, { headers });
      toast.success('Numéro de compteur Linky enregistré');
      setEditingLinky(false);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSavingLinky(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Administrateur', color: 'bg-purple-100 text-purple-700 border-purple-200' },
      pro: { label: 'Producteur Pro', color: 'bg-amber-100 text-amber-700 border-amber-200' },
      client: { label: 'Client', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    };
    const b = badges[role] || badges.client;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${b.color}`} data-testid="role-badge">
        {b.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  const u = profile?.user || {};
  const isProducer = u.user_type === 'producer' || u.role === 'pro';
  const isAdmin = u.role === 'admin';

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="back-dashboard">
            <ArrowLeft className="w-5 h-5 text-green-700" />
            <span className="font-semibold text-green-700">Retour au Dashboard</span>
          </Link>
          <div className="flex items-center gap-3">
            {isProducer && (
              <Button onClick={() => navigate('/pro')} variant="outline" className="rounded-full border-green-200 text-green-700 hover:bg-green-50" data-testid="go-pro-dashboard">
                <Sun className="w-4 h-4 mr-2" /> Mes Clients
              </Button>
            )}
            {isAdmin && (
              <Button onClick={() => navigate('/admin')} variant="outline" className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="go-admin">
                <Shield className="w-4 h-4 mr-2" /> Admin
              </Button>
            )}
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#052e16] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Mon Profil
              </h1>
              <p className="text-[#64748b] mt-1">Gérez vos informations personnelles</p>
            </div>
            {getRoleBadge(u.role)}
          </div>

          {/* Alert banner if profile incomplete */}
          {!profileComplete && !isAdmin && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="profile-incomplete-alert">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-amber-800">Profil incomplet</p>
                <p className="text-sm text-amber-700 mt-1">
                  Pour pouvoir acheter ou vendre de l'énergie, vous devez renseigner obligatoirement :
                </p>
                <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                  {missingFields.includes('linky_serial') && <li>Votre numéro de série de compteur Linky</li>}
                  {missingFields.includes('rib') && <li>Vos coordonnées bancaires (RIB)</li>}
                </ul>
              </div>
            </div>
          )}

          {/* Profile Card */}
          <Card className="bg-white border border-green-100 shadow-sm rounded-2xl" data-testid="profile-card">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Informations personnelles
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(!editing)}
                className="text-green-700 hover:bg-green-50 rounded-full"
                data-testid="edit-profile-btn"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                {editing ? 'Annuler' : 'Modifier'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">Nom</Label>
                  {editing ? (
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="rounded-xl border-green-200 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 h-12"
                      data-testid="profile-name-input"
                    />
                  ) : (
                    <div className="flex items-center gap-2 h-12 px-4 bg-[#f0fdf4] rounded-xl">
                      <User className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-[#052e16]" data-testid="profile-name">{u.name}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">Email</Label>
                  <div className="flex items-center gap-2 h-12 px-4 bg-[#f0fdf4] rounded-xl">
                    <Mail className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-[#052e16]" data-testid="profile-email">{u.email}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">Adresse</Label>
                  {editing ? (
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="rounded-xl border-green-200 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 h-12"
                      data-testid="profile-address-input"
                    />
                  ) : (
                    <div className="flex items-center gap-2 h-12 px-4 bg-[#f0fdf4] rounded-xl">
                      <MapPin className="w-4 h-4 text-green-600" />
                      <span className="text-[#052e16]" data-testid="profile-address">{formData.address || 'Non renseigné'}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">Téléphone</Label>
                  {editing ? (
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="rounded-xl border-green-200 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 h-12"
                      data-testid="profile-phone-input"
                    />
                  ) : (
                    <div className="flex items-center gap-2 h-12 px-4 bg-[#f0fdf4] rounded-xl">
                      <Phone className="w-4 h-4 text-green-600" />
                      <span className="text-[#052e16]" data-testid="profile-phone">{formData.phone || 'Non renseigné'}</span>
                    </div>
                  )}
                </div>
              </div>

              {editing && (
                <div className="flex justify-end pt-4 border-t border-green-100">
                  <Button
                    onClick={handleSave}
                    className="bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20 rounded-full px-6"
                    data-testid="save-profile-btn"
                  >
                    <Save className="w-4 h-4 mr-2" /> Enregistrer
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-3 gap-6" data-testid="profile-stats">
            {isProducer ? (
              <>
                <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                      <User className="w-5 h-5 text-amber-600" />
                    </div>
                    <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="stat-total-clients">
                      {stats.total_clients || 0}
                    </p>
                    <p className="text-sm text-[#64748b]">Clients enregistrés</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mb-3">
                      <Zap className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="stat-sold-kwh">
                      {(stats.total_sold_kwh || 0).toFixed(1)} kWh
                    </p>
                    <p className="text-sm text-[#64748b]">Énergie vendue</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="stat-revenue">
                      {(stats.total_revenue || 0).toFixed(2)} €
                    </p>
                    <p className="text-sm text-[#64748b]">Revenus générés</p>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
                      <Zap className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="stat-bought-kwh">
                      {(stats.total_bought_kwh || 0).toFixed(1)} kWh
                    </p>
                    <p className="text-sm text-[#64748b]">Énergie achetée</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mb-3">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="stat-spent">
                      {(stats.total_spent || 0).toFixed(2)} €
                    </p>
                    <p className="text-sm text-[#64748b]">Total dépensé</p>
                  </CardContent>
                </Card>
                <Card className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mb-3">
                      <Activity className="w-5 h-5 text-purple-600" />
                    </div>
                    <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="stat-type">
                      {u.user_type === 'producer' ? 'Producteur' : 'Consommateur'}
                    </p>
                    <p className="text-sm text-[#64748b]">Type de compte</p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* RIB / Coordonnées bancaires */}
          <Card className="bg-white border border-green-100 shadow-sm rounded-2xl" data-testid="rib-card">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <CreditCard className="w-5 h-5 text-green-600" />
                Coordonnées bancaires (RIB)
                {!ribSaved && !isAdmin && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Obligatoire</span>
                )}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingRib(!editingRib)}
                className="text-green-700 hover:bg-green-50 rounded-full"
                data-testid="edit-rib-btn"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                {editingRib ? 'Annuler' : ribSaved ? 'Modifier' : 'Ajouter'}
              </Button>
            </CardHeader>
            <CardContent>
              {ribSaved && !editingRib ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-green-700 font-medium">RIB enregistré</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">IBAN</Label>
                      <div className="h-10 px-4 bg-[#f0fdf4] rounded-xl flex items-center">
                        <span className="font-mono text-sm text-[#052e16]" data-testid="rib-iban-masked">{ribSaved.iban_masked}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">BIC</Label>
                      <div className="h-10 px-4 bg-[#f0fdf4] rounded-xl flex items-center">
                        <span className="font-mono text-sm text-[#052e16]" data-testid="rib-bic">{ribSaved.bic}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">Titulaire</Label>
                      <div className="h-10 px-4 bg-[#f0fdf4] rounded-xl flex items-center">
                        <span className="text-sm text-[#052e16]" data-testid="rib-titulaire">{ribSaved.titulaire}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-[#64748b]">
                    {isProducer ? 'Vos revenus de vente seront virés sur ce compte.' : 'Vos achats seront prélevés sur ce compte.'}
                  </p>
                </div>
              ) : editingRib ? (
                <div className="space-y-4">
                  <p className="text-sm text-[#64748b]">
                    {isProducer
                      ? 'Entrez votre RIB pour recevoir les virements de vos ventes.'
                      : 'Entrez votre RIB pour payer vos achats par prélèvement.'}
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">IBAN *</Label>
                      <Input
                        value={ribData.iban}
                        onChange={(e) => setRibData({ ...ribData, iban: e.target.value })}
                        placeholder="FR76 1234 5678 9012 3456 7890 123"
                        className="rounded-xl border-green-200 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 h-10 font-mono text-sm"
                        data-testid="rib-iban-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">BIC *</Label>
                      <Input
                        value={ribData.bic}
                        onChange={(e) => setRibData({ ...ribData, bic: e.target.value })}
                        placeholder="BNPAFRPP"
                        className="rounded-xl border-green-200 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 h-10 font-mono text-sm"
                        data-testid="rib-bic-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">Titulaire *</Label>
                      <Input
                        value={ribData.titulaire}
                        onChange={(e) => setRibData({ ...ribData, titulaire: e.target.value })}
                        placeholder="Jean Dupont"
                        className="rounded-xl border-green-200 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 h-10"
                        data-testid="rib-titulaire-input"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={handleSaveRib}
                      disabled={savingRib}
                      className="bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20 rounded-full px-6"
                      data-testid="save-rib-btn"
                    >
                      <Save className="w-4 h-4 mr-2" /> Enregistrer le RIB
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <CreditCard className="w-10 h-10 text-green-300 mx-auto mb-3" />
                  <p className="text-sm text-[#64748b] mb-3">Aucun RIB enregistré</p>
                  <p className="text-xs text-[#64748b]">
                    {isProducer
                      ? 'Ajoutez votre RIB pour recevoir vos paiements de vente.'
                      : 'Ajoutez votre RIB pour payer vos achats par prélèvement.'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Compteur Linky */}
          {!isAdmin && (
            <Card className="bg-white border border-green-100 shadow-sm rounded-2xl" data-testid="linky-card">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Gauge className="w-5 h-5 text-green-600" />
                  Compteur Linky
                  {!linkySerial && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Obligatoire</span>
                  )}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingLinky(!editingLinky)}
                  className="text-green-700 hover:bg-green-50 rounded-full"
                  data-testid="edit-linky-btn"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  {editingLinky ? 'Annuler' : linkySerial ? 'Modifier' : 'Ajouter'}
                </Button>
              </CardHeader>
              <CardContent>
                {linkySerial && !editingLinky ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-green-700 font-medium">Compteur Linky enregistré</span>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">Numéro de série</Label>
                      <div className="h-10 px-4 bg-[#f0fdf4] rounded-xl flex items-center">
                        <span className="font-mono text-sm text-[#052e16]" data-testid="linky-serial-display">{linkySerial}</span>
                      </div>
                    </div>
                    <p className="text-xs text-[#64748b]">
                      Ce numéro permet de relier votre compteur à la communauté d'autoconsommation.
                    </p>
                  </div>
                ) : editingLinky ? (
                  <div className="space-y-4">
                    <p className="text-sm text-[#64748b]">
                      Entrez le numéro de série de votre compteur Linky (visible sur le compteur ou sur votre facture).
                    </p>
                    <div className="max-w-md space-y-2">
                      <Label className="text-xs uppercase tracking-widest font-semibold text-[#64748b]">Numéro de série *</Label>
                      <Input
                        value={linkySerial}
                        onChange={(e) => setLinkySerial(e.target.value)}
                        placeholder="Ex: 09 461 234 567 890"
                        className="rounded-xl border-green-200 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 h-10 font-mono text-sm"
                        data-testid="linky-serial-input"
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={handleSaveLinky}
                        disabled={savingLinky}
                        className="bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20 rounded-full px-6"
                        data-testid="save-linky-btn"
                      >
                        <Save className="w-4 h-4 mr-2" /> Enregistrer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Gauge className="w-10 h-10 text-amber-300 mx-auto mb-3" />
                    <p className="text-sm text-amber-700 font-medium mb-1">Numéro de compteur non renseigné</p>
                    <p className="text-xs text-[#64748b]">
                      Ce champ est obligatoire pour participer à la coopérative énergétique.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card className="bg-white border border-green-100 shadow-sm rounded-2xl" data-testid="quick-actions">
            <CardHeader>
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Actions rapides
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {isProducer && (
                  <Button
                    variant="outline"
                    className="h-auto p-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300 rounded-xl border-green-100"
                    onClick={() => navigate('/pro')}
                    data-testid="action-manage-clients"
                  >
                    <User className="w-6 h-6 text-green-600" />
                    <span className="font-medium text-[#052e16]">Gérer mes clients</span>
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300 rounded-xl border-green-100"
                  onClick={() => navigate('/consumption')}
                  data-testid="action-consumption"
                >
                  <Activity className="w-6 h-6 text-green-600" />
                  <span className="font-medium text-[#052e16]">Ma consommation</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300 rounded-xl border-green-100"
                  onClick={() => navigate('/wallet')}
                  data-testid="action-wallet"
                >
                  <TrendingUp className="w-6 h-6 text-green-600" />
                  <span className="font-medium text-[#052e16]">Mon compte</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* RGPD Section */}
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl" data-testid="rgpd-section">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Shield className="w-5 h-5 text-muted-foreground" />
                Vos donnees personnelles (RGPD)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Conformement au RGPD, vous disposez d'un droit d'acces, de portabilite et d'effacement de vos donnees personnelles.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-300 rounded-xl border-slate-200"
                  onClick={async () => {
                    try {
                      const headers = getAuthHeaders();
                      const res = await axios.get(`${API_URL}/api/users/me/data-export`, { headers });
                      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `green-teva-export-donnees-${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Export de vos donnees telecharge');
                    } catch { toast.error('Erreur lors de l\'export'); }
                  }}
                  data-testid="rgpd-export-data"
                >
                  <Download className="w-6 h-6 text-blue-600" />
                  <span className="font-medium">Exporter mes donnees</span>
                  <span className="text-xs text-muted-foreground">Droit d'acces (Art. 15)</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-center gap-2 hover:bg-red-50 hover:border-red-300 rounded-xl border-slate-200"
                  onClick={async () => {
                    if (!window.confirm('Etes-vous sur de vouloir supprimer votre compte ? Cette action est irreversible. Vos donnees personnelles seront supprimees et vos transactions anonymisees.')) return;
                    if (!window.confirm('DERNIERE CONFIRMATION : Cette action supprimera definitivement votre compte et toutes vos donnees personnelles.')) return;
                    try {
                      const headers = getAuthHeaders();
                      await axios.delete(`${API_URL}/api/users/me/account`, { headers });
                      toast.success('Compte supprime avec succes');
                      logout();
                      navigate('/');
                    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de la suppression'); }
                  }}
                  data-testid="rgpd-delete-account"
                >
                  <Trash2 className="w-6 h-6 text-red-500" />
                  <span className="font-medium text-red-600">Supprimer mon compte</span>
                  <span className="text-xs text-muted-foreground">Droit a l'effacement (Art. 17)</span>
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <a href="/confidentialite" className="underline hover:text-foreground">Politique de confidentialite</a>
                <a href="/cgu" className="underline hover:text-foreground">CGU</a>
                <a href="/cgv" className="underline hover:text-foreground">CGV</a>
                <a href="/mentions-legales" className="underline hover:text-foreground">Mentions legales</a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
